/**
 * Plot symbol icons, generated as small canvas images and registered with
 * MapLibre so symbol layers can use them with `icon-image`. This keeps the
 * symbols crisp at any zoom (pixel-sized) and rotated to heading where
 * needed, without depending on font glyphs that the demo glyph server
 * doesn't ship.
 *
 * Paper-chart conventions used:
 *   - boat       : filled triangle pointing along heading (Ardent arrow)
 *   - fix        : unfilled circle (position fix)
 *   - waypoint   : unfilled square
 *   - ep         : unfilled triangle (estimated position)
 *   - dr         : a "+" (dead reckoning)
 */
import type { Map as MaplibreMap } from 'maplibre-gl';

const SIZE = 32; // px square — small but crisp at default DPI

interface IconSpec {
  id: string;
  draw: (ctx: CanvasRenderingContext2D, accent: string, fg: string) => void;
}

/**
 * Red for a position we are not standing behind. Bright enough to catch the eye
 * against the day palette's white shallows and the night palette's near-black,
 * and red because that is what the rest of the app already uses for "do not
 * trust this yet".
 */
const UNFIXED_RED = '#e5342b';

/**
 * The Ardent mark: an arrowhead with a swallowtail base, the notch at the bottom
 * lifting to a point. Shared so the live boat and the unfixed one can never drift
 * into being different shapes.
 */
function drawArdentMark(ctx: CanvasRenderingContext2D, fill: string): void {
  const cx = SIZE / 2;
  const cy = SIZE / 2;
  const r = SIZE * 0.4;
  ctx.beginPath();
  ctx.moveTo(cx, cy - r); // apex, the bow
  ctx.lineTo(cx + r * 0.72, cy + r * 0.8); // starboard quarter
  ctx.lineTo(cx, cy + r * 0.3); // the swallowtail notch
  ctx.lineTo(cx - r * 0.72, cy + r * 0.8); // port quarter
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  // A white keyline so the mark holds its shape against dark chart fills and
  // satellite imagery alike.
  ctx.lineWidth = 1.8;
  ctx.lineJoin = 'round';
  ctx.strokeStyle = 'rgba(255,255,255,0.95)';
  ctx.stroke();
}

const SPECS: IconSpec[] = [
  {
    id: 'plot-boat',
    draw: (ctx, accent) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const r = SIZE * 0.38;
      // Triangle pointing up (north). Rotation is applied by the symbol layer.
      ctx.beginPath();
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx - r * 0.65, cy + r * 0.7);
      ctx.lineTo(cx + r * 0.65, cy + r * 0.7);
      ctx.closePath();
      ctx.fillStyle = accent;
      ctx.fill();
      ctx.lineWidth = 1.8;
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.stroke();
    },
  },
  {
    // The Ardent mark: an arrowhead with a swallowtail base, the notch at the
    // bottom lifting to a point. The logo draws it as an outline within an
    // outline; at map-icon size that detail turns to mush, so this is the outer
    // silhouette alone, which is what carries the recognition anyway.
    id: 'plot-boat-ardent',
    draw: (ctx, accent) => drawArdentMark(ctx, accent),
  },
  {
    // The same mark, in red, for when the position under it is not a live fix:
    // the phone's cached starting position before our own first fix, or a fix
    // that has gone stale. It used to be the ordinary boat at 40% opacity, and a
    // faded boat on a busy chart is easy to look straight past. Red is not.
    //
    // The shape is deliberately identical. This is the same boat in the same
    // place; the only thing being said is how much of it to believe.
    id: 'plot-boat-ardent-unfixed',
    draw: (ctx) => drawArdentMark(ctx, UNFIXED_RED),
  },
  {
    // A sailing yacht from above: fine pointed bow, beamiest just forward of
    // midships, tucked-in rounded stern, and a white mast dot where the rig
    // stands. The silhouette a sailor draws when asked to draw their own deck.
    id: 'plot-boat-sail',
    draw: (ctx, accent) => {
      const s = SIZE / 24;
      ctx.beginPath();
      ctx.moveTo(12 * s, 2.5 * s);
      ctx.bezierCurveTo(16.5 * s, 6 * s, 17 * s, 12 * s, 15.5 * s, 19 * s);
      ctx.bezierCurveTo(14.5 * s, 20.6 * s, 9.5 * s, 20.6 * s, 8.5 * s, 19 * s);
      ctx.bezierCurveTo(7 * s, 12 * s, 7.5 * s, 6 * s, 12 * s, 2.5 * s);
      ctx.closePath();
      ctx.fillStyle = accent;
      ctx.fill();
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(12 * s, 10.5 * s, 1.3 * s, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.fill();
    },
  },
  {
    // A motor cruiser from above: rounded shoulders at the bow, full beam
    // carried all the way aft to a square transom, with the cabin drawn as a
    // white outline block. Boxy on purpose; that is the recognition.
    id: 'plot-boat-motor',
    draw: (ctx, accent) => {
      const s = SIZE / 24;
      ctx.beginPath();
      ctx.moveTo(12 * s, 3 * s);
      ctx.bezierCurveTo(16 * s, 4 * s, 17 * s, 8 * s, 17 * s, 12 * s);
      ctx.lineTo(17 * s, 20 * s);
      ctx.lineTo(7 * s, 20 * s);
      ctx.lineTo(7 * s, 12 * s);
      ctx.bezierCurveTo(7 * s, 8 * s, 8 * s, 4 * s, 12 * s, 3 * s);
      ctx.closePath();
      ctx.fillStyle = accent;
      ctx.fill();
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.stroke();
      ctx.beginPath();
      ctx.rect(9.2 * s, 9.5 * s, 5.6 * s, 6.5 * s);
      ctx.lineWidth = 1.3;
      ctx.stroke();
    },
  },
  {
    // A planing powerboat from above: a lean dart, needle bow, widest right at
    // the transom. Distinct from the cruiser by rake and from the yacht by
    // where the beam sits.
    id: 'plot-boat-power',
    draw: (ctx, accent) => {
      const s = SIZE / 24;
      ctx.beginPath();
      ctx.moveTo(12 * s, 2.2 * s);
      ctx.bezierCurveTo(13.6 * s, 6 * s, 15.4 * s, 13 * s, 15.8 * s, 20 * s);
      ctx.lineTo(8.2 * s, 20 * s);
      ctx.bezierCurveTo(8.6 * s, 13 * s, 10.4 * s, 6 * s, 12 * s, 2.2 * s);
      ctx.closePath();
      ctx.fillStyle = accent;
      ctx.fill();
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.stroke();
    },
  },
  {
    /**
     * An anchor, drawn rather than typed.
     *
     * It used to be the character U+2693 in a text layer, and it did not draw at
     * all: the map's glyphs come from a font stack that has no such character, so
     * where the hook went down there was nothing on the chart. The anchor watch
     * had a boundary, a swing track and an alarm, and no anchor.
     *
     * A drawn symbol has no font behind it to be missing, works with no signal,
     * and takes the palette accent like every other plot icon. The shape is the
     * chart convention: ring, shank, stock across it, and arms curving up to the
     * flukes.
     */
    id: 'plot-anchor',
    draw: (ctx, accent) => {
      const cx = SIZE / 2;
      const top = SIZE * 0.14;
      const bottom = SIZE * 0.86;
      const ringR = SIZE * 0.1;
      const armHalf = SIZE * 0.28;

      ctx.strokeStyle = accent;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      // A white keyline underneath the whole thing, so it holds against dark
      // fills and satellite imagery the same way the boat does.
      for (const pass of [0, 1] as const) {
        ctx.strokeStyle = pass === 0 ? 'rgba(255,255,255,0.95)' : accent;
        ctx.lineWidth = pass === 0 ? 4.4 : 2.4;

        // Ring at the head.
        ctx.beginPath();
        ctx.arc(cx, top + ringR, ringR, 0, Math.PI * 2);
        ctx.stroke();

        // Shank, from under the ring down to the crown.
        ctx.beginPath();
        ctx.moveTo(cx, top + ringR * 2);
        ctx.lineTo(cx, bottom);
        ctx.stroke();

        // Stock, across the shank just below the ring.
        ctx.beginPath();
        ctx.moveTo(cx - SIZE * 0.19, top + ringR * 2.6);
        ctx.lineTo(cx + SIZE * 0.19, top + ringR * 2.6);
        ctx.stroke();

        // Arms: one sweep from fluke to fluke through the crown.
        ctx.beginPath();
        ctx.moveTo(cx - armHalf, bottom - SIZE * 0.16);
        ctx.quadraticCurveTo(cx - armHalf * 0.55, bottom, cx, bottom);
        ctx.quadraticCurveTo(cx + armHalf * 0.55, bottom, cx + armHalf, bottom - SIZE * 0.16);
        ctx.stroke();
      }
    },
  },
  {
    /**
     * A note marker: three bars, the usual "there is writing here" mark.
     *
     * It was the character U+2261 in a text layer, and Open Sans has no such
     * character, so pilotage notes were marked on the chart with nothing at all.
     * Found while shipping the glyph ranges: worth checking every character we
     * type into a map, because a font either has it or the label silently is not
     * there.
     */
    id: 'plot-note',
    draw: (ctx, accent) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const w = SIZE * 0.28;
      const gap = SIZE * 0.13;
      for (const pass of [0, 1] as const) {
        ctx.strokeStyle = pass === 0 ? 'rgba(255,255,255,0.95)' : accent;
        ctx.lineWidth = pass === 0 ? 4.6 : 2.6;
        ctx.lineCap = 'round';
        for (const dy of [-gap, 0, gap]) {
          ctx.beginPath();
          ctx.moveTo(cx - w, cy + dy);
          ctx.lineTo(cx + w, cy + dy);
          ctx.stroke();
        }
      }
    },
  },
  {
    id: 'plot-fix',
    draw: (ctx, _accent, fg) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const r = SIZE * 0.3;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.lineWidth = 2;
      ctx.strokeStyle = fg;
      ctx.stroke();
    },
  },
  {
    id: 'plot-waypoint',
    draw: (ctx, accent) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const r = SIZE * 0.28;
      ctx.beginPath();
      ctx.rect(cx - r, cy - r, r * 2, r * 2);
      ctx.lineWidth = 2.2;
      ctx.strokeStyle = accent;
      ctx.stroke();
    },
  },
  {
    id: 'plot-ep',
    draw: (ctx, _accent, fg) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const r = SIZE * 0.34;
      ctx.beginPath();
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx - r * 0.866, cy + r * 0.5);
      ctx.lineTo(cx + r * 0.866, cy + r * 0.5);
      ctx.closePath();
      ctx.lineWidth = 2;
      ctx.strokeStyle = fg;
      ctx.stroke();
    },
  },
  {
    id: 'plot-dr',
    draw: (ctx, _accent, fg) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const r = SIZE * 0.3;
      ctx.beginPath();
      ctx.moveTo(cx - r, cy);
      ctx.lineTo(cx + r, cy);
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx, cy + r);
      ctx.lineWidth = 2.4;
      ctx.lineCap = 'round';
      ctx.strokeStyle = fg;
      ctx.stroke();
    },
  },
  {
    id: 'plot-arrow',
    draw: (ctx, _accent, fg) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const r = SIZE * 0.36;
      // Filled arrowhead pointing up (placed along the line so rotated by
      // MapLibre's symbol-placement to follow the line direction).
      ctx.beginPath();
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx - r * 0.55, cy + r * 0.35);
      ctx.lineTo(cx, cy + r * 0.1);
      ctx.lineTo(cx + r * 0.55, cy + r * 0.35);
      ctx.closePath();
      ctx.fillStyle = fg;
      ctx.fill();
    },
  },
  {
    // Passage destination. Deliberately NOT the thin black charted-anchorage
    // glyph: this is a filled, accent-coloured disc with a white anchor on top,
    // so a destination never reads as a charted anchorage. Drawn big and bold.
    id: 'plot-destination',
    draw: (ctx, accent) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const r = SIZE * 0.42;
      // Coloured disc with a white rim.
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = accent;
      ctx.fill();
      ctx.lineWidth = 1.8;
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.stroke();
      // White anchor glyph on top.
      const a = SIZE * 0.26; // anchor half-height
      ctx.strokeStyle = 'rgba(255,255,255,0.98)';
      ctx.fillStyle = 'rgba(255,255,255,0.98)';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      // shank
      ctx.beginPath();
      ctx.moveTo(cx, cy - a * 0.78);
      ctx.lineTo(cx, cy + a * 0.85);
      ctx.stroke();
      // ring at the top
      ctx.beginPath();
      ctx.arc(cx, cy - a * 0.92, a * 0.2, 0, Math.PI * 2);
      ctx.stroke();
      // stock (crossbar)
      ctx.beginPath();
      ctx.moveTo(cx - a * 0.55, cy - a * 0.28);
      ctx.lineTo(cx + a * 0.55, cy - a * 0.28);
      ctx.stroke();
      // flukes (curved arms at the bottom)
      ctx.beginPath();
      ctx.arc(cx, cy + a * 0.1, a * 0.62, Math.PI * 0.15, Math.PI * 0.85);
      ctx.stroke();
      // fluke tips
      ctx.beginPath();
      ctx.moveTo(cx - a * 0.6, cy + a * 0.5);
      ctx.lineTo(cx - a * 0.6, cy + a * 0.18);
      ctx.moveTo(cx + a * 0.6, cy + a * 0.5);
      ctx.lineTo(cx + a * 0.6, cy + a * 0.18);
      ctx.stroke();
    },
  },
  {
    // A five-pointed star, for a favourite spot or anything worth flagging.
    id: 'plot-star',
    draw: (ctx, accent) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const R = SIZE * 0.34;
      const r = R * 0.42;
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const ang = -Math.PI / 2 + (i * Math.PI) / 5;
        const rad = i % 2 === 0 ? R : r;
        const x = cx + Math.cos(ang) * rad;
        const y = cy + Math.sin(ang) * rad;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fillStyle = accent;
      ctx.fill();
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.stroke();
    },
  },
  {
    // A plain anchor outline (an anchorage or a spot to lie), drawn in the accent
    // colour without the destination disc so it reads as a mark, not the passage
    // destination.
    id: 'plot-anchor',
    draw: (ctx, accent) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const a = SIZE * 0.3;
      ctx.strokeStyle = accent;
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      // ring
      ctx.beginPath();
      ctx.arc(cx, cy - a * 0.95, a * 0.22, 0, Math.PI * 2);
      ctx.stroke();
      // shank
      ctx.beginPath();
      ctx.moveTo(cx, cy - a * 0.7);
      ctx.lineTo(cx, cy + a * 0.9);
      ctx.stroke();
      // stock (crossbar)
      ctx.beginPath();
      ctx.moveTo(cx - a * 0.5, cy - a * 0.25);
      ctx.lineTo(cx + a * 0.5, cy - a * 0.25);
      ctx.stroke();
      // flukes (curved arms)
      ctx.beginPath();
      ctx.arc(cx, cy + a * 0.15, a * 0.68, Math.PI * 0.15, Math.PI * 0.85);
      ctx.stroke();
      // fluke tips
      ctx.beginPath();
      ctx.moveTo(cx - a * 0.66, cy + a * 0.55);
      ctx.lineTo(cx - a * 0.66, cy + a * 0.2);
      ctx.moveTo(cx + a * 0.66, cy + a * 0.55);
      ctx.lineTo(cx + a * 0.66, cy + a * 0.2);
      ctx.stroke();
    },
  },
  {
    // A warning triangle with an exclamation, for a hazard we want to flag.
    id: 'plot-hazard',
    draw: (ctx, accent) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const r = SIZE * 0.4;
      ctx.beginPath();
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx + r * 0.9, cy + r * 0.62);
      ctx.lineTo(cx - r * 0.9, cy + r * 0.62);
      ctx.closePath();
      ctx.fillStyle = accent;
      ctx.fill();
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = 'rgba(255,255,255,0.95)';
      ctx.stroke();
      // white exclamation
      ctx.strokeStyle = 'rgba(255,255,255,0.98)';
      ctx.fillStyle = 'rgba(255,255,255,0.98)';
      ctx.lineWidth = 2.4;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(cx, cy - r * 0.28);
      ctx.lineTo(cx, cy + r * 0.2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy + r * 0.42, 1.4, 0, Math.PI * 2);
      ctx.fill();
    },
  },
  {
    // A simple fish, for a fishing mark or pot.
    id: 'plot-fish',
    draw: (ctx, accent) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const r = SIZE * 0.3;
      ctx.strokeStyle = accent;
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      // body
      ctx.beginPath();
      ctx.moveTo(cx + r, cy);
      ctx.quadraticCurveTo(cx, cy - r * 0.75, cx - r * 0.4, cy);
      ctx.quadraticCurveTo(cx, cy + r * 0.75, cx + r, cy);
      ctx.stroke();
      // tail fin
      ctx.beginPath();
      ctx.moveTo(cx - r * 0.4, cy);
      ctx.lineTo(cx - r, cy - r * 0.5);
      ctx.moveTo(cx - r * 0.4, cy);
      ctx.lineTo(cx - r, cy + r * 0.5);
      ctx.stroke();
      // eye
      ctx.beginPath();
      ctx.arc(cx + r * 0.5, cy - r * 0.12, 1.1, 0, Math.PI * 2);
      ctx.fillStyle = accent;
      ctx.fill();
    },
  },
  {
    // A small navigation buoy: a body with a short mast and a diamond topmark.
    id: 'plot-buoy',
    draw: (ctx, accent) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2 + SIZE * 0.06;
      const r = SIZE * 0.2;
      // body
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = accent;
      ctx.fill();
      ctx.lineWidth = 1.4;
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.stroke();
      // mast
      ctx.strokeStyle = accent;
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx, cy - r - SIZE * 0.2);
      ctx.stroke();
      // diamond topmark
      const t = cy - r - SIZE * 0.2;
      const sm = SIZE * 0.075;
      ctx.beginPath();
      ctx.moveTo(cx, t - sm);
      ctx.lineTo(cx + sm, t);
      ctx.lineTo(cx, t + sm);
      ctx.lineTo(cx - sm, t);
      ctx.closePath();
      ctx.fillStyle = accent;
      ctx.fill();
    },
  },
  {
    // A little post-it / sticky note dropped on the chart. A folded yellow
    // square (the classic sticky-note fold in the top-right corner) with a
    // couple of text lines, so it reads as "a note pinned here" at a glance,
    // distinct from a waypoint square or a hazard triangle. The body follows the
    // accent so it stays readable in any palette; the fold is a shade darker.
    id: 'plot-note',
    draw: (ctx, accent) => {
      const cx = SIZE / 2;
      const cy = SIZE / 2;
      const half = SIZE * 0.28;
      const fold = half * 0.55;
      const left = cx - half;
      const right = cx + half;
      const top = cy - half;
      const bottom = cy + half;
      // The note body: a square with the top-right corner cut off for the fold.
      ctx.beginPath();
      ctx.moveTo(left, top);
      ctx.lineTo(right - fold, top);
      ctx.lineTo(right, top + fold);
      ctx.lineTo(right, bottom);
      ctx.lineTo(left, bottom);
      ctx.closePath();
      ctx.fillStyle = '#ffd54a'; // sticky-note yellow, fixed so a note always reads as a note
      ctx.fill();
      ctx.lineWidth = 1.4;
      ctx.strokeStyle = 'rgba(0,0,0,0.55)';
      ctx.stroke();
      // The folded corner (a small darker triangle in the top-right).
      ctx.beginPath();
      ctx.moveTo(right - fold, top);
      ctx.lineTo(right - fold, top + fold);
      ctx.lineTo(right, top + fold);
      ctx.closePath();
      ctx.fillStyle = '#e0ac1c';
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.4)';
      ctx.stroke();
      // A couple of "text" lines so it reads as writing on the note.
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1.6;
      ctx.lineCap = 'round';
      const lx0 = left + half * 0.35;
      const lx1 = right - half * 0.35;
      for (let k = 0; k < 2; k++) {
        const ly = cy - half * 0.05 + k * half * 0.5;
        ctx.beginPath();
        ctx.moveTo(lx0, ly);
        ctx.lineTo(k === 1 ? cx : lx1, ly); // second line a bit shorter
        ctx.stroke();
      }
    },
  },
  {
    // The MOB mark dropped on the chart. The classic orange-and-white ring
    // lifebuoy ("perry buoy"), the same shape as the MOB button, so the on-chart
    // mark reads as the exact thing the button dropped. Fixed orange/white so it
    // stays a life-ring on any base and in any palette (a safety mark shouldn't
    // recolour with the theme). Drawn to the same proportions as LifebuoyIcon.
    id: 'plot-mob',
    draw: (ctx) => {
      const s = SIZE / 24; // the icon is authored on a 24-unit grid, like the SVG
      const cx = 12 * s;
      const cy = 12 * s;
      // Orange ring.
      ctx.beginPath();
      ctx.arc(cx, cy, 8 * s, 0, Math.PI * 2);
      ctx.lineWidth = 3.6 * s;
      ctx.strokeStyle = '#E8552B';
      ctx.lineCap = 'round';
      ctx.stroke();
      // Four white grab-bands at N/E/S/W across the ring.
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3.6 * s;
      ctx.lineCap = 'butt';
      const band = (x1: number, y1: number, x2: number, y2: number) => {
        ctx.beginPath();
        ctx.moveTo(x1 * s, y1 * s);
        ctx.lineTo(x2 * s, y2 * s);
        ctx.stroke();
      };
      band(12, 2.2, 12, 5.8);
      band(12, 18.2, 12, 21.8);
      band(2.2, 12, 5.8, 12);
      band(18.2, 12, 21.8, 12);
    },
  },
];

// Latest palette colours, so the on-demand supplier can redraw a dropped icon in
// the current theme even between explicit registerPlotIcons calls.
let iconColors = { accent: '#c62828', fg: '#000000' };

const specById = new Map<string, IconSpec>(SPECS.map((s) => [s.id, s]));

/** True if `id` is one of our plot overlay icons (boat, fix, waypoint, ep, dr, …). */
export function isPlotIconId(id: string): boolean {
  return specById.has(id);
}

/** Render one plot icon to a MapLibre image in the current palette, or null. */
function renderPlotImage(id: string): { width: number; height: number; data: Uint8Array } | null {
  const spec = specById.get(id);
  if (!spec) return null;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.clearRect(0, 0, SIZE, SIZE);
  spec.draw(ctx, iconColors.accent, iconColors.fg);
  return {
    width: SIZE,
    height: SIZE,
    data: new Uint8Array(ctx.getImageData(0, 0, SIZE, SIZE).data.buffer),
  };
}

/**
 * Register all plot icons with the map. Idempotent - safe to call again
 * after a palette swap or style reload. Records the palette so the on-demand
 * supplier can redraw any dropped icon in the same theme.
 */
export function registerPlotIcons(map: MaplibreMap, accent: string, fg: string): void {
  iconColors = { accent, fg };
  for (const spec of SPECS) {
    if (map.hasImage(spec.id)) map.removeImage(spec.id);
    const img = renderPlotImage(spec.id);
    if (img) map.addImage(spec.id, img);
  }
}

const suppliedMaps = new WeakSet<MaplibreMap>();

/**
 * On-demand supplier for the plot icons. Whenever a symbol layer references a plot-*
 * image that is not currently registered — after a WebGL context loss drops the GPU
 * texture, an image eviction, or a race between adding the image and the layer —
 * MapLibre fires 'styleimagemissing' and we redraw the icon in the current palette.
 * This is the bulletproof fix for "the boat symbol stops drawing while the fill/line
 * layers (e.g. the heading cone) still show": the image reappears the instant it is
 * needed, independent of any self-heal timing. Idempotent per map, and the listener
 * lives on the Map so it survives setStyle (palette swaps) and context loss.
 */
export function installPlotIconSupplier(map: MaplibreMap): void {
  if (suppliedMaps.has(map)) return;
  suppliedMaps.add(map);
  map.on('styleimagemissing', (e) => {
    const id = e.id;
    if (!specById.has(id) || map.hasImage(id)) return;
    const img = renderPlotImage(id);
    if (img) map.addImage(id, img);
  });
}
